import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, IconButton, LinearProgress, MenuItem,
  Skeleton, Snackbar, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { useDropzone } from 'react-dropzone';
import {
  BsDownload, BsFileEarmarkText, BsPencil, BsTrash, BsUpload,
} from 'react-icons/bs';

import axiosInstance from '../../axiosInstance';
import DocumentFil from '../DocumentFil';
import Fiche from '../Expert/Fiche';
import OrganisePhotos from './OrganisePhotos';
import { themeExpert } from '../../theme/expert';

const PAR_PAGE = 200;

const CATEGORIES = [
  ['facture', 'Facture'], ['devis', 'Devis'],
  ['photo', 'Photo'], ['autre', 'Autre'],
];

const ROTATIONS = [[0, '0°'], [90, '90°'], [180, '180°'], [270, '270°']];

const TYPES_ACCEPTES = {
  'image/*': ['.jpg', '.jpeg', '.png'],
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.oasis.opendocument.text': ['.odt'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.oasis.opendocument.spreadsheet': ['.ods'],
  'message/rfc822': ['.eml'],
};

function ListFile() {
  const { expertiseId } = useParams();

  const [pieces, setPieces] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');

  const [apercu, setApercu] = useState(null);
  const [edition, setEdition] = useState(null);
  const [aSupprimer, setASupprimer] = useState(null);
  const [depot, setDepot] = useState(false);
  const [organiser, setOrganiser] = useState(false);
  const [filVisible, setFilVisible] = useState(false);

  const [aEnvoyer, setAEnvoyer] = useState([]);
  const [avancement, setAvancement] = useState({});
  const [corrige, setCorrige] = useState(false);
  const [occupe, setOccupe] = useState(false);
  const surveillerJusqua = useRef(0);

  // `silencieux` : le contrôle en tâche de fond ne pose pas le voile de
  // chargement, et ne touche à la liste que si une pièce a changé.
  const charger = useCallback(async ({ silencieux = false } = {}) => {
    try {
      const { data } = await axiosInstance.get('/files', {
        params: { expertise_id: expertiseId, limit: PAR_PAGE, page: 1 },
        silencieux,
      });
      const recues = Array.isArray(data.results) ? data.results
        : (Array.isArray(data) ? data : []);
      setPieces((etat) => (
        JSON.stringify(etat) === JSON.stringify(recues) ? etat : recues));
      setErreur('');
    } catch {
      // Un contrôle raté n'est pas une panne : le suivant réessaiera.
      if (!silencieux) {
        setErreur("Les pièces du dossier n'ont pas pu être chargées.");
      }
    } finally {
      setChargement(false);
    }
  }, [expertiseId]);

  useEffect(() => { charger(); }, [charger]);

  // Tant qu'un document est à l'OCR — et deux minutes après un dépôt, le temps
  // qu'il démarre — la liste se rafraîchit seule.
  const enTraitement = pieces.some((p) => p.processing_status === 'processing');
  useEffect(() => {
    if (!enTraitement && Date.now() >= surveillerJusqua.current) return undefined;
    const minuterie = setInterval(() => charger({ silencieux: true }), 5000);
    return () => clearInterval(minuterie);
  }, [enTraitement, charger]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: setAEnvoyer, accept: TYPES_ACCEPTES,
  });

  // Une pièce est une image ou un document, jamais les deux : trier sur deux
  // tests indépendants faisait apparaître certaines pièces dans les deux
  // listes, et d'autres dans aucune.
  const images = pieces.filter((p) => p.file);
  const documents = pieces.filter((p) => !p.file);

  const envoyer = () => {
    surveillerJusqua.current = Date.now() + 120000;
    aEnvoyer.forEach((fichier) => {
      const courriel = fichier.name.toLowerCase().endsWith('.eml');
      const chemin = courriel ? 'ajout_email' : 'ajout_document';
      const corps = new FormData();
      corps.append(courriel ? 'file' : 'files', fichier, fichier.name);

      setAvancement((etat) => ({ ...etat,
                                 [fichier.name]: { part: 0, etat: 'en cours' } }));

      axiosInstance.post(`/files/${expertiseId}/${chemin}/`, corps, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => setAvancement((etat) => ({
          ...etat,
          [fichier.name]: { part: Math.round((e.loaded * 100) / e.total),
                            etat: 'en cours' },
        })),
      })
        .then(() => {
          setAvancement((etat) => ({ ...etat,
                                     [fichier.name]: { part: 100, etat: 'reçu' } }));
          charger();
        })
        .catch(() => setAvancement((etat) => ({
          ...etat, [fichier.name]: { part: 0, etat: 'échec' },
        })));
    });
  };

  const telecharger = async (piece) => {
    try {
      const { data } = await axiosInstance.get(`/pdf/${piece.id}/`,
                                               { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([data]));
      const lien = document.createElement('a');
      lien.href = url;
      // L'adresse est signée : l'extension est avant « ?e=…&s=… ».
      lien.download = `${piece.title}.${(piece.fichier || '').split('?')[0].split('.').pop()}`;
      document.body.appendChild(lien);
      lien.click();
      lien.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setErreur("Le fichier n'a pas pu être téléchargé.");
    }
  };

  const corriger = async () => {
    setCorrige(true);
    try {
      const { data } = await axiosInstance.post(`/files/${edition.id}/correct/`,
                                                { text: edition.description });
      setEdition((etat) => ({ ...etat,
                              description: data.correction || etat.description }));
    } catch {
      setErreur("La correction n'a pas abouti.");
    } finally {
      setCorrige(false);
    }
  };

  const enregistrer = async (e) => {
    e.preventDefault();
    setOccupe(true);
    try {
      const corps = new FormData();
      ['title', 'description', 'category', 'rotation', 'group', 'share']
        .forEach((champ) => corps.append(champ, edition[champ] ?? ''));
      // PATCH : le fichier lui-même n'est pas renvoyé, et un PUT prétendrait
      // remplacer la pièce entière.
      const { data } = await axiosInstance.patch(`/files/${edition.id}/`, corps);
      setPieces((etat) => etat.map((p) => (p.id === data.id ? data : p)));
      setEdition(null);
      setMessage('Pièce enregistrée');
    } catch {
      setErreur("La pièce n'a pas pu être enregistrée.");
    } finally {
      setOccupe(false);
    }
  };

  const supprimer = async () => {
    setOccupe(true);
    try {
      await axiosInstance.delete(`/files/${aSupprimer.id}/`);
      setPieces((etat) => etat.filter((p) => p.id !== aSupprimer.id));
      setASupprimer(null);
      setMessage('Pièce supprimée');
    } catch {
      setErreur("La pièce n'a pas pu être supprimée.");
    } finally {
      setOccupe(false);
    }
  };

  const ouvrirEdition = (piece) => setEdition({
    id: piece.id, file: piece.file || '', title: piece.title || '',
    description: piece.description || '', category: piece.category || 'autre',
    rotation: String(piece.rotation ?? 0), share: Boolean(piece.share),
    group: piece.group ?? 99,
  });

  return (
    <>
      <Stack spacing={3}>
        {erreur && <Alert severity="error"
                          onClose={() => setErreur('')}>{erreur}</Alert>}

        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
          <Button variant="contained" startIcon={<BsUpload />}
                  onClick={() => setDepot(true)}>
            Déposer des pièces
          </Button>
          <Button variant="outlined" onClick={() => setFilVisible(!filVisible)}>
            {filVisible ? 'Masquer le fil' : 'Afficher le fil'}
          </Button>
          {images.length > 0 && (
            <Button variant="outlined" onClick={() => setOrganiser(true)}>
              Organiser les photos
            </Button>
          )}
        </Stack>

        {filVisible && (
          <DocumentFil expertiseId={expertiseId} onSuccess={() => charger()} />
        )}

        {chargement && <Skeleton height={200} />}

        {!chargement && pieces.length === 0 && (
          <Fiche>
            <Typography variant="body2" color="text.secondary"
                        sx={{ textAlign: 'center', py: 4 }}>
              Aucune pièce déposée pour ce dossier.
            </Typography>
          </Fiche>
        )}

        {images.length > 0 && (
          <Fiche titre={`Photos (${images.length})`}>
            <Box sx={{ display: 'grid', gap: 2,
                       gridTemplateColumns:
                         'repeat(auto-fill, minmax(170px, 1fr))' }}>
              {images.map((piece) => (
                <Box key={piece.id}>
                  <Box
                    component="img"
                    src={piece.thumbnail || piece.file}
                    alt={piece.title || ''}
                    loading="lazy"
                    onClick={() => setApercu(piece.file)}
                    sx={{ width: '100%', aspectRatio: '1', objectFit: 'cover',
                          borderRadius: 1, cursor: 'zoom-in', display: 'block',
                          border: '1px solid', borderColor: 'divider' }}
                  />
                  <Stack direction="row" alignItems="center"
                         justifyContent="space-between" sx={{ mt: 0.5 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" noWrap component="div">
                        {piece.title || 'Sans titre'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ordre {piece.group ?? '—'}
                      </Typography>
                    </Box>
                    <Stack direction="row">
                      <IconButton size="small" aria-label="Modifier"
                                  onClick={() => ouvrirEdition(piece)}>
                        <BsPencil />
                      </IconButton>
                      <IconButton size="small" aria-label="Supprimer"
                                  onClick={() => setASupprimer(piece)}>
                        <BsTrash />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Box>
              ))}
            </Box>
          </Fiche>
        )}

        {documents.length > 0 && (
          <Fiche titre={`Documents (${documents.length})`} sx={{ p: 0 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Titre</TableCell>
                    <TableCell>Texte extrait</TableCell>
                    <TableCell align="right">&nbsp;</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {documents.map((piece) => (
                    <TableRow key={piece.id} hover>
                      <TableCell>{piece.title || 'Sans titre'}</TableCell>
                      <TableCell>
                        {piece.processing_status === 'processing' ? (
                          <Chip size="small" label="en cours" />
                        ) : piece.has_text ? (
                          <Chip size="small" color="success" label="oui" />
                        ) : (
                          <Box component="span"
                               sx={{ color: 'text.disabled' }}>—</Box>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title="Modifier">
                          <IconButton size="small"
                                      onClick={() => ouvrirEdition(piece)}>
                            <BsPencil />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Télécharger">
                          <IconButton size="small"
                                      onClick={() => telecharger(piece)}>
                            <BsDownload />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Ouvrir dans la GED">
                          <IconButton size="small" onClick={() => window.open(
                            `/ged/file/${piece.id}`, 'GedExtract',
                            'width=1200,height=900,scrollbars=yes,resizable=yes')}>
                            <BsFileEarmarkText />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer">
                          <IconButton size="small"
                                      onClick={() => setASupprimer(piece)}>
                            <BsTrash />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Fiche>
        )}
      </Stack>

      {/* Les fenêtres se rendent dans un portail, hors de la coquille. */}
      <ThemeProvider theme={themeExpert}>
        <Dialog open={Boolean(apercu)} onClose={() => setApercu(null)}
                maxWidth="xl" fullWidth>
          <Box sx={{ bgcolor: '#000', display: 'flex',
                     justifyContent: 'center', alignItems: 'center' }}>
            <Box component="img" src={apercu || ''} alt=""
                 sx={{ maxWidth: '100%', maxHeight: '85vh',
                       objectFit: 'contain' }} />
          </Box>
        </Dialog>

        <Dialog open={edition !== null} onClose={() => setEdition(null)}
                fullWidth maxWidth="lg">
          <Box component="form" onSubmit={enregistrer} noValidate>
            <DialogTitle>Modifier la pièce</DialogTitle>
            <DialogContent>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}
                     sx={{ pt: 1 }}>
                {edition?.file && (
                  <Box component="img" src={edition.file} alt=""
                       sx={{ width: { md: '40%' }, maxHeight: 420,
                             objectFit: 'contain', alignSelf: 'flex-start',
                             borderRadius: 1 }} />
                )}

                <Stack spacing={2.5} sx={{ flexGrow: 1 }}>
                  <TextField label="Titre" value={edition?.title || ''}
                             onChange={(e) => setEdition(
                               (s) => ({ ...s, title: e.target.value }))} />

                  <TextField
                    label="Description"
                    multiline
                    minRows={3}
                    value={edition?.description || ''}
                    onChange={(e) => setEdition(
                      (s) => ({ ...s, description: e.target.value }))}
                    helperText="Ce que montre la pièce. C'est ce texte qui sera repris au rapport."
                  />
                  <Box>
                    <Button size="small" variant="outlined" onClick={corriger}
                            disabled={corrige || !edition?.description}>
                      {corrige ? 'Correction…' : "Corriger le texte par l'IA"}
                    </Button>
                  </Box>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField select label="Catégorie"
                               value={edition?.category || 'autre'}
                               onChange={(e) => setEdition(
                                 (s) => ({ ...s, category: e.target.value }))}>
                      {CATEGORIES.map(([valeur, libelle]) => (
                        <MenuItem key={valeur} value={valeur}>{libelle}</MenuItem>
                      ))}
                    </TextField>

                    <TextField select label="Rotation"
                               value={edition?.rotation ?? '0'}
                               onChange={(e) => setEdition(
                                 (s) => ({ ...s, rotation: e.target.value }))}>
                      {ROTATIONS.map(([valeur, libelle]) => (
                        <MenuItem key={valeur} value={String(valeur)}>
                          {libelle}
                        </MenuItem>
                      ))}
                    </TextField>

                    <TextField label="Ordre" type="number"
                               value={edition?.group ?? ''}
                               onChange={(e) => setEdition(
                                 (s) => ({ ...s, group: e.target.value }))}
                               sx={{ maxWidth: { sm: 120 } }} />
                  </Stack>

                  <FormControlLabel
                    control={<Checkbox checked={Boolean(edition?.share)}
                                       onChange={(e) => setEdition(
                                         (s) => ({ ...s, share: e.target.checked }))} />}
                    label="Visible par ceux avec qui le dossier est partagé"
                  />
                </Stack>
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setEdition(null)} disabled={occupe}>
                Annuler
              </Button>
              <Button type="submit" variant="contained" disabled={occupe}>
                {occupe ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
            </DialogActions>
          </Box>
        </Dialog>

        <Dialog open={depot} onClose={() => setDepot(false)}
                fullWidth maxWidth="sm">
          <DialogTitle>Déposer des pièces</DialogTitle>
          <DialogContent>
            <Box
              {...getRootProps()}
              sx={{ border: '2px dashed', borderRadius: 1, p: 5, mt: 1,
                    textAlign: 'center', cursor: 'pointer',
                    borderColor: isDragActive ? 'primary.main' : 'divider',
                    bgcolor: isDragActive ? 'action.hover'
                                          : 'background.default' }}
            >
              <input {...getInputProps()} />
              <Typography variant="body2">
                Glissez vos fichiers ici, ou cliquez pour les choisir
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Photos, PDF, Word, Excel, et courriels .eml
              </Typography>
            </Box>

            {aEnvoyer.length > 0 && (
              <Typography variant="body2" sx={{ mt: 2 }}>
                {aEnvoyer.length} fichier{aEnvoyer.length > 1 ? 's' : ''} prêt
                {aEnvoyer.length > 1 ? 's' : ''} à partir.
              </Typography>
            )}

            {Object.entries(avancement).map(([nom, { part, etat }]) => (
              <Box key={nom} sx={{ mt: 2 }}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" noWrap
                              sx={{ maxWidth: '70%' }}>{nom}</Typography>
                  <Typography variant="caption"
                              color={etat === 'échec' ? 'error.main'
                                                      : 'text.secondary'}>
                    {etat}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={part}
                  color={etat === 'échec' ? 'error'
                    : (etat === 'reçu' ? 'success' : 'primary')}
                />
              </Box>
            ))}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setDepot(false)}>Fermer</Button>
            <Button variant="contained" onClick={envoyer}
                    disabled={aEnvoyer.length === 0}>
              Envoyer
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={aSupprimer !== null} onClose={() => setASupprimer(null)}>
          <DialogTitle>Supprimer cette pièce ?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              « {aSupprimer?.title || 'Sans titre'} » sera retirée du dossier.
              C'est définitif, et une pièce supprimée ne peut plus être citée
              au rapport.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setASupprimer(null)} disabled={occupe}>
              Annuler
            </Button>
            <Button onClick={supprimer} color="error" variant="contained"
                    disabled={occupe}>
              Supprimer
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>

      <OrganisePhotos show={organiser} onHide={() => setOrganiser(false)}
                      imageFiles={images} onSaved={() => charger()} />

      <Snackbar open={Boolean(message)} autoHideDuration={3000}
                onClose={() => setMessage('')} message={message}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </>
  );
}

export default ListFile;
